/**
 * v7.17 — Z-API (WhatsApp) via Backend Proxy.
 *
 * Os tokens Z-API (Instance Token, Client Token) ficam APENAS no servidor
 * (env vars > Mongo `app_config`). O navegador chama o endpoint proxy
 * `/api/integrations/zapi/send-text` e o backend assina/encaminha a
 * requisição para api.z-api.io.
 *
 * Estratégia MOCK: se o backend responde `{ mocked: true, reason:
 * 'no_credentials' }`, persiste localmente em `db.notifications` com flag
 * mocked para que o Log do Admin continue mostrando tudo.
 */
import db from '@/db';
import { normalizeBrPhone } from '@/lib/notifications';
import { loadServerConfig } from '@/lib/serverConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache em memória para `isZapiConfigured` — evita refazer fetch a cada chamada.
let _flagsCache = null;
let _flagsCacheAt = 0;
const FLAGS_TTL_MS = 30_000;

async function _readFlags({ force = false } = {}) {
  if (!force && _flagsCache && Date.now() - _flagsCacheAt < FLAGS_TTL_MS) {
    return _flagsCache;
  }
  try {
    const body = await loadServerConfig();
    _flagsCache = body?.flags || {};
    _flagsCacheAt = Date.now();
    return _flagsCache;
  } catch {
    return _flagsCache || {};
  }
}

export function invalidateZapiCache() {
  _flagsCache = null;
  _flagsCacheAt = 0;
}

export async function isZapiConfigured() {
  const flags = await _readFlags();
  return !!flags.hasZapiInstanceToken;
}

/**
 * Envia mensagem de texto via Backend Proxy.
 * Retorna { sent, mocked, raw?, error?, status? }.
 */
export async function sendWhatsAppText({ to, message, orderId, status, skipPersist = false }) {
  const normalized = normalizeBrPhone(to);
  if (!normalized) {
    return { sent: false, mocked: false, error: `Telefone inválido: "${to}"` };
  }
  const payloadRecord = {
    to: normalized,
    message,
    orderId,
    status,
    sentAt: new Date().toISOString(),
  };

  if (!API_URL) {
    return { sent: false, mocked: false, error: 'REACT_APP_BACKEND_URL ausente' };
  }

  try {
    const resp = await fetch(`${API_URL}/api/integrations/zapi/send-text`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: normalized, message }),
    });
    const rawText = await resp.text();
    let body = {};
    try { body = rawText ? JSON.parse(rawText) : {}; } catch { /* não-JSON */ }

    // Proxy MOCK (credenciais ausentes no servidor)
    if (body?.mocked) {
      if (!skipPersist) {
        try { await db.notifications.add({ ...payloadRecord, trackingUrl: '', mocked: true }); } catch {}
      }
      return { sent: false, mocked: true };
    }

    const ok = resp.ok && body?.sent === true;

    if (!ok) {
      // eslint-disable-next-line no-console
      console.warn('[Z-API PROXY ERROR]', {
        status: resp.status,
        body: rawText.slice(0, 400),
      });
    }

    const detailedError = ok ? undefined : (
      body?.error
      || (resp.status === 400 ? `Requisição rejeitada (HTTP 400). Causas comuns: número inválido (DDD+9 dígitos), mensagem vazia, instância desconectada.` : '')
      || (resp.status === 401 ? 'Credenciais Z-API inválidas no servidor.' : '')
      || (resp.status === 403 ? 'Acesso negado pela Z-API — instância pode estar desconectada.' : '')
      || (resp.status === 404 ? 'Instance-ID não encontrado.' : '')
      || (resp.status >= 500 ? `Servidor instável (HTTP ${resp.status}).` : '')
      || `HTTP ${resp.status} sem detalhe.`
    );

    if (!skipPersist) {
      try {
        await db.notifications.add({
          ...payloadRecord,
          trackingUrl: '',
          mocked: false,
          delivered: ok,
          zapiRaw: body?.raw || body,
        });
      } catch {}
    }
    return { sent: ok, mocked: false, raw: body?.raw, status: resp.status, error: detailedError };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[Z-API PROXY] erro de rede:', err);
    const msg = String(err?.message || err);
    const friendly = /failed to fetch|networkerror/i.test(msg)
      ? 'Sem conexão com o backend Kaven. Verifique sua internet.'
      : msg;
    if (!skipPersist) {
      try {
        await db.notifications.add({ ...payloadRecord, trackingUrl: '', mocked: false, delivered: false, error: msg });
      } catch {}
    }
    return { sent: false, mocked: false, error: friendly };
  }
}

/**
 * Atalhos prontos para o fluxo do pedido:
 *  - Pedido Recebido (após Pix confirmado)
 *  - Saiu para Entrega (quando entregador inicia rota)
 */
function buildTrackingUrl(orderId) {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/rastreio/${orderId}`;
  }
  return `/rastreio/${orderId}`;
}

export async function notifyOrderReceived(order) {
  const trackingUrl = buildTrackingUrl(order.mpPaymentId || order.id);
  const msg =
    `✅ *KAVEN - Pedido Confirmado*\n\n` +
    `Olá ${order.customerName}! Recebemos seu pedido *#${order.id}* no valor de *R$ ${Number(order.total || 0).toFixed(2).replace('.', ',')}*.\n` +
    `Já estamos preparando com carinho!\n\n` +
    `📍 Acompanhe em tempo real:\n${trackingUrl}\n\n` +
    `_Mensagem automática - Kaven Delivery_`;
  return sendWhatsAppText({
    to: order.customerPhone || order.phone || '',
    message: msg,
    orderId: order.id,
    status: 'confirmed',
  });
}

export async function notifyOrderDelivering(order) {
  const trackingUrl = buildTrackingUrl(order.mpPaymentId || order.id);
  // v7.35 — Inclui o PIN de entrega APENAS na mensagem do cliente.
  // O entregador NÃO recebe o PIN por nenhum canal (toast, WhatsApp, push).
  // Segurança: só quem realmente é o destinatário tem o código.
  const pinBlock = order.deliveryPin
    ? (
        `\n🔐 *CÓDIGO DE ENTREGA: ${order.deliveryPin}*\n` +
        `_Informe esse código ao entregador quando ele chegar._\n`
      )
    : '';
  const msg =
    `🛵 *KAVEN - Saiu para Entrega*\n\n` +
    `${order.customerName}, seu pedido *#${order.id}* saiu pra entrega! O entregador já está a caminho de ${order.neighborhood || 'seu bairro'}.\n` +
    pinBlock +
    `\n📍 Acompanhe em tempo real:\n${trackingUrl}\n\n` +
    `_Mensagem automática - Kaven Delivery_`;
  return sendWhatsAppText({
    to: order.customerPhone || order.phone || '',
    message: msg,
    orderId: order.id,
    status: 'delivering',
  });
}
